// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FooterChat from "@steiner/XDL/Icons/FooterChat";
import FooterContactIcon from "@steiner/XDL/Icons/FooterContactIcon";
import FooterPhone from "@steiner/XDL/Icons/FooterPhone";
import React from "react";

const enum fields {
    title = "title",
    links = "links",
}

const enum icons {
    phone = "phone",
    chat = "chat",
    contact = "contact",
}

export interface LinkModel {
    fields: {
        destination: LinkFieldValue;
        overriddenTitle: string;
        icon: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.links]: LinkModel[];
    };
}

const getIcon = (icon: string) => {
    switch (icon) {
        case icons.phone:
            return FooterPhone;
        case icons.chat:
            return FooterChat;
        case icons.contact:
            return FooterContactIcon;
        default:
            break;
    }
    return "";
};

const getAriaLabel = (icon: string, overriddenTitle?: string, value?: string) => {
    switch (icon) {
        case icons.phone:
            return `${translate("Phone")}: ${value}`;
        default:
            return overriddenTitle;
    }
};

export interface FooterContactStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    linksWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const footerContactStyles: FooterContactStyles = {};

const styles = footerContactStyles;
const FooterContact = ({ fields }: OwnProps) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="Footer-Contact">
            {fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
            <StyledWrapper {...styles.linksWrapper}>
                {links.map((link, index) => {
                    const { overriddenTitle, icon } = fields.links[index].fields;

                    return (
                        <Link
                            {...styles.link}
                            key={`${link.url}_${index}`}
                            href={link.url}
                            aria-label={getAriaLabel(icon, overriddenTitle, link.url)}
                            icon={{
                                iconProps: {
                                    size: 14,
                                    src: getIcon(icon),
                                },
                                position: "left",
                            }}
                        >
                            {overriddenTitle}
                        </Link>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterContact,
    definition: {
        group: "Footer",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overriddenTitle } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overriddenTitle) {
                            return overriddenTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "icon",
                        displayName: "Select Icon",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Phone", value: icons.phone },
                            { displayName: "Chat", value: icons.chat },
                            { displayName: "Contact Us", value: icons.contact },
                        ],
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
